
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useCar, Car, CarIssue } from './CarContext';

export interface ShareLink {
  id: string;
  token: string;
  carId: string;
  isEditable: boolean;
  expiresAt: string; // ISO date string
  createdAt: string; // ISO date string
}

interface MechanicShareContextType {
  shareLinks: ShareLink[];
  createShareLink: (carId: string, isEditable: boolean, expirationHours: number) => ShareLink;
  getShareLinkByToken: (token: string) => ShareLink | undefined;
  deleteShareLink: (id: string) => void;
  isLinkValid: (token: string) => boolean;
  logMechanicRepair: (
    token: string, 
    repairData: { 
      title: string;
      description: string;
      date: string;
      cost?: number;
      mechanicName: string;
      garageName?: string;
      receiptImage?: string;
      resolvedIssues?: string[];
      partiallyResolvedIssues?: string[];
    }
  ) => string | null;
  logMechanicSymptom: (
    token: string,
    symptomData: {
      title: string;
      description: string;
      date: string;
      mechanicName: string;
      garageName?: string;
      images?: string[];
    }
  ) => string | null;
}

const MechanicShareContext = createContext<MechanicShareContextType | undefined>(undefined);

export const useMechanicShare = () => {
  const context = useContext(MechanicShareContext);
  if (!context) {
    throw new Error('useMechanicShare must be used within a MechanicShareProvider');
  }
  return context;
};

export const MechanicShareProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { addRepair, addIssue, cars } = useCar();
  
  const [shareLinks, setShareLinks] = useState<ShareLink[]>(() => {
    const savedLinks = localStorage.getItem('mechanicShareLinks');
    return savedLinks ? JSON.parse(savedLinks) : [];
  });

  useEffect(() => {
    localStorage.setItem('mechanicShareLinks', JSON.stringify(shareLinks));
  }, [shareLinks]);

  const createShareLink = (carId: string, isEditable: boolean, expirationHours: number = 24): ShareLink => {
    const now = new Date();
    const expiresAt = new Date(now);
    expiresAt.setHours(expiresAt.getHours() + expirationHours);
    
    const newLink: ShareLink = {
      id: crypto.randomUUID(),
      token: crypto.randomUUID(),
      carId,
      isEditable,
      expiresAt: expiresAt.toISOString(),
      createdAt: now.toISOString(),
    };
    
    setShareLinks([...shareLinks, newLink]);
    return newLink;
  };

  const getShareLinkByToken = (token: string): ShareLink | undefined => {
    return shareLinks.find(link => link.token === token);
  };

  const deleteShareLink = (id: string) => {
    setShareLinks(shareLinks.filter(link => link.id !== id));
  };

  const isLinkValid = (token: string): boolean => {
    const link = getShareLinkByToken(token);
    if (!link) return false;
    
    const now = new Date();
    const expiresAt = new Date(link.expiresAt);
    return expiresAt > now;
  };

  const logMechanicRepair = (
    token: string, 
    repairData: { 
      title: string;
      description: string;
      date: string;
      cost?: number;
      mechanicName: string;
      garageName?: string;
      receiptImage?: string;
      resolvedIssues?: string[];
      partiallyResolvedIssues?: string[];
    }
  ): string | null => {
    const link = getShareLinkByToken(token);
    if (!link || !link.isEditable || !isLinkValid(token)) return null;
    
    const repairId = addRepair(link.carId, {
      title: repairData.title,
      date: repairData.date,
      description: repairData.description || '',
      cost: repairData.cost,
      mechanicName: repairData.mechanicName,
      receiptImage: repairData.receiptImage,
      resolvedIssues: repairData.resolvedIssues || [],
      partiallyResolvedIssues: repairData.partiallyResolvedIssues || [],
      addedByMechanic: true,
      mechanicAddedDate: new Date().toISOString(),
    });
    
    // If this was a one-time use link (e.g., for a specific repair), delete it
    // Uncomment if you want links to expire after one use
    // deleteShareLink(link.id);
    
    return repairId;
  };

  const logMechanicSymptom = (
    token: string,
    symptomData: {
      title: string;
      description: string;
      date: string;
      mechanicName: string;
      garageName?: string;
      images?: string[];
    }
  ): string | null => {
    const link = getShareLinkByToken(token);
    if (!link || !link.isEditable || !isLinkValid(token)) return null;
    
    const symptomId = addIssue(link.carId, {
      title: symptomData.title,
      description: symptomData.description,
      date: symptomData.date,
      images: symptomData.images || [],
      status: 'draft',
      notes: `Added by ${symptomData.mechanicName}${symptomData.garageName ? ` from ${symptomData.garageName}` : ''} via mechanic link`,
    });
    
    // If this was a one-time use link, delete it
    // Uncomment if you want links to expire after one use
    // deleteShareLink(link.id);
    
    return symptomId;
  };

  return (
    <MechanicShareContext.Provider value={{
      shareLinks,
      createShareLink,
      getShareLinkByToken,
      deleteShareLink,
      isLinkValid,
      logMechanicRepair,
      logMechanicSymptom,
    }}>
      {children}
    </MechanicShareContext.Provider>
  );
};
